import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { STORAGE_TOKENS } from "../lib/storageTokens";
import { toast } from "@/shared/widgets/toast";
import { getErrorMessage } from "../lib/utils";

interface AuthAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
  suppressRedirectOnFail?: boolean;
  suppressErrorToast?: boolean;
}

type RequestQueue = {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  config: AuthAxiosRequestConfig;
}[];

let isRefreshing = false;
let failedQueue: RequestQueue = [];

const logAuthRedirect = (
  reason: string,
  error?: AxiosError,
  config?: AuthAxiosRequestConfig
) => {
  if (typeof window === "undefined") {
    return;
  }

  const targetConfig = config || (error?.config as AuthAxiosRequestConfig | undefined);
  const metadata = {
    reason,
    requestUrl: targetConfig?.url ?? error?.config?.url,
    requestMethod: (targetConfig?.method ?? error?.config?.method)?.toUpperCase(),
    responseStatus: error?.response?.status,
  };

  // Helps track which request is pushing the app back to /auth
  console.warn("[auth] redirecting to /auth", metadata);
};

const redirectToAuth = () => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(STORAGE_TOKENS.USER_DATA);

  if (window.location.pathname.includes("/auth")) {
    return;
  }

  window.location.href = "/auth";
};

const client = Axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: "application/json, text/plain, */*",
  },
  withCredentials: true,
});

client.defaults.withCredentials = true;

const processQueue = (error: Error | null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      client(config).then(resolve).catch(reject);
    }
  });

  failedQueue = [];
};

const getErrorMessageFromResponse = (error: AxiosError): string => {
  const responseData = error.response?.data;
  
  if (typeof responseData === 'object' && responseData !== null) {
    const data = responseData as Record<string, unknown>;
    if (typeof data.message === 'string') {
      return data.message;
    }
    if (typeof data.error === 'string') {
      return data.error;
    }
  }
  
  return getErrorMessage(error);
};

const getStatusMessage = (status: number): { title: string; description: string } => {
  switch (status) {
    case 400:
      return {
        title: "Requisição inválida",
        description: "Os dados enviados estão incorretos. Verifique e tente novamente.",
      };
    case 401:
      return {
        title: "Não autorizado",
        description: "Sua sessão expirou. Faça login novamente.",
      };
    case 403:
      return {
        title: "Acesso negado",
        description: "Você não tem permissão para realizar esta ação.",
      };
    case 404:
      return {
        title: "Não encontrado",
        description: "O recurso solicitado não foi encontrado.",
      };
    case 413:
      return {
        title: "Arquivo muito grande",
        description: "O arquivo enviado excede o tamanho máximo permitido. Tente enviar um arquivo menor.",
      };
    case 422:
      return {
        title: "Dados inválidos",
        description: "Os dados fornecidos não são válidos. Verifique e tente novamente.",
      };
    case 429:
      return {
        title: "Muitas requisições",
        description: "Você fez muitas requisições. Aguarde um momento e tente novamente.",
      };
    case 500:
      return {
        title: "Erro no servidor",
        description: "Ocorreu um erro interno. Tente novamente mais tarde.",
      };
    case 502:
    case 503:
    case 504:
      return {
        title: "Serviço indisponível",
        description: "O serviço está temporariamente indisponível. Tente novamente mais tarde.",
      };
    default:
      return {
        title: "Erro na requisição",
        description: "Ocorreu um erro ao processar sua solicitação.",
      };
  }
};

client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = (error.config || {}) as AuthAxiosRequestConfig;

    // Tratar erros específicos com mensagens apropriadas
    if (status && status !== 401) {
      const errorMessage = getErrorMessageFromResponse(error);
      const statusMessage = getStatusMessage(status);
      
      // Exibir toast com mensagem específica (a menos que seja suprimido)
      if (typeof window !== "undefined" && !originalRequest?.suppressErrorToast) {
        toast.api.error(
          statusMessage.title,
          errorMessage !== statusMessage.description ? errorMessage : statusMessage.description
        );
      }
      
      return Promise.reject(error);
    }

    // Tratamento especial para 401 (autenticação)

    if (!originalRequest || originalRequest.skipAuthRefresh) {
      logAuthRedirect("skipAuthRefresh request or missing config", error, originalRequest);
      // redirectToAuth();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      if (!originalRequest.suppressRedirectOnFail) {
        logAuthRedirect("request already retried once", error, originalRequest);
        // redirectToAuth();
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    originalRequest.suppressRedirectOnFail = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    return client
      .request({
        url: "/v1/auth/refresh-web",
        method: "POST",
        withCredentials: true,
        skipAuthRefresh: true,
      } as AuthAxiosRequestConfig)
      .then(() => {
        processQueue(null);
        return client(originalRequest);
      })
      .catch((refreshError) => {
        const err =
          refreshError instanceof Error
            ? refreshError
            : new Error("Unable to refresh session");
        processQueue(err);
        logAuthRedirect(
          "refresh flow failed",
          Axios.isAxiosError(refreshError) ? refreshError : undefined,
          originalRequest
        );
        // redirectToAuth();
        return Promise.reject(err);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }
);

const api = async <T = unknown>(options: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await client({
      ...options,
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    if (
      axiosError.response?.status === 401 &&
      !(axiosError.config as AuthAxiosRequestConfig)?.skipAuthRefresh &&
      !(axiosError.config as AuthAxiosRequestConfig)?.suppressRedirectOnFail
    ) {
      logAuthRedirect("api helper caught 401 outside interceptor", axiosError);
      // redirectToAuth();
    }

    return Promise.reject({
      message: axiosError.message,
      code: axiosError.code,
      response: axiosError.response,
    });
  }
};

export { client };
export default api;
